// mountType.ts — Mount-type classification for panel feature entities
// Categorises each feature group by how it relates to the panel surface:
//   void   — full cut-through (openings, blockouts)
//   recess — flush / recessed into surface (feature strips, chamfers, dock leveler pits)
//   proud  — protruding from surface (pilasters, lintels, top plate, ledger bar)
//   embed  — cast into concrete, sub-surface (pick points, brace points, weld connections, dowels)

export type MountType = 'void' | 'recess' | 'proud' | 'embed';

/** Map every slot group key to its mount classification. */
export const MOUNT_TYPE_MAP: Record<string, MountType> = {
  // Voids — full cut-through
  rovar: 'void',
  wdvar: 'void',
  drvar: 'void',
  rbvar: 'void',
  sbvar: 'void',
  // Recessed — flush into surface
  fhvar: 'recess',
  fvvar: 'recess',
  dlvar: 'recess',
  chvar: 'recess',
  tsvar: 'recess',
  fsvar: 'recess',
  ssvar: 'recess',
  // Proud — protruding from surface
  plvar: 'proud',
  llvar: 'proud',
  tpvar: 'proud',
  lbvar: 'proud',
  // Embedded — cast into concrete
  ppvar: 'embed',
  bpvar: 'embed',
  sdvar: 'embed',
  wcvar: 'embed',
};

export const MOUNT_TYPE_LABELS: Record<MountType, string> = {
  void:   'Voids',
  recess: 'Recess',
  proud:  'Proud',
  embed:  'Embed',
};

export const MOUNT_TYPE_COLORS: Record<MountType, string> = {
  void:   '#1a2840',  // dark navy (matches opening colour)
  recess: '#c8a520',  // gold (matches feature colour)
  proud:  '#8b7355',  // warm brown
  embed:  '#ff6000',  // orange
};

export const MOUNT_TYPES: MountType[] = ['void', 'recess', 'proud', 'embed'];

/** Get mount type for an entity ID like 'rovar-0' or 'panel-body'. */
export function getMountType(entityId: string): MountType | null {
  const dash = entityId.lastIndexOf('-');
  const group = dash > 0 ? entityId.slice(0, dash) : entityId;
  return MOUNT_TYPE_MAP[group] ?? null;
}
